/*
 * Copyright (c) 2024. World Wide Generation Ltd
 */

import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Collapse } from 'reactstrap';
import { Card } from '@g17eco/atoms/card';
import type { MetricReference } from '@g17eco/types/insights-synthesis';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';
import { ROUTES } from '../../../../../constants/routes';
import { generateUrl } from '../../../../../routes/util';
import { DATE, formatDateUTC } from '../../../../../utils/date';
import { UniversalTrackerValueView } from '../../../../../features/universal-tracker-value';
import { isStaff } from '../../../../../selectors/user';
import { useAppSelector } from '../../../../../reducers';
import { FeatureStability } from '../../../../../molecules/feature-stability';

interface Props {
  reference: MetricReference;
  index: number;
  initiativeId: string;
}

export const ReferenceRow: React.FC<Props> = ({ reference, index, initiativeId }) => {
  const [promptContextOpen, setPromptContextOpen] = useState(false);
  const isUserStaff = useAppSelector(isStaff);
  // Get the most recent value (or first value if available)
  const values = reference.values ?? [];

  const utr = reference.utr;
  const invalidValues = values.filter(value => !value.utrv);
  if (invalidValues.length > 0) {
    return (
      <Card className='p-3'>
        <div className='d-flex align-items-start gap-3'>
          <div className='text-lg mt-2 d-flex align-items-center justify-content-center rounded-circle background-ThemeInfoLight text-ThemeInfoDark strong' style={{ width: '2rem', height: '2rem', flexShrink: 0 }}>
            {index}
          </div>
        </div>
      </Card>
    );
  }

  return (
    <Card className='p-3'>
      <div className='d-flex align-items-start gap-3'>
        <div
          className='text-lg mt-2 d-flex align-items-center justify-content-center rounded-circle background-ThemeInfoLight text-ThemeInfoDark strong'
          style={{ width: '2rem', height: '2rem', flexShrink: 0 }}
        >
          {index}
        </div>

        <div className='d-flex flex-column gap-2 w-100'>

          <div className='d-flex mb-2'>
            <div className='flex-fill'>
              <h6 className='text-lg text-ThemeTextDark strong mb-0'>{reference.utr.name}</h6>
            </div>
            <div className='mt-1'>
              <SimpleTooltip text='Relevance: How strongly this data point supports the insight'>
                <div className='text-ThemeTextMedium'>
                  <i className='fas fa-star me-1 text-ThemeWarningMedium' />
                  Relevance: {Math.round(reference.relevance * 100)}%
                </div>
              </SimpleTooltip>
            </div>
          </div>

          {values.filter(value => value.utrv).map((value) => (
            <div key={value.utrvId} className='d-flex flex-column gap-2'>
              <div className='d-flex flex-wrap gap-2 text-ThemeTextMedium'>
                {value.utrv.effectiveDate && (
                  <div className='text-lg'>
                    <i className='fas fa-clock me-1' />
                    <span className='strong'>{formatDateUTC(value.utrv.effectiveDate, DATE.MONTH_YEAR)}</span>
                  </div>
                )}
                {value.utrv.period && (
                  <div className='d-flex align-items-center'>
                    <i className='fas fa-chart-bar me-1' />
                    Period: <span className='strong ms-1 text-capitalize'>{value.utrv.period}</span>
                  </div>
                )}
                <div className='ms-auto align-items-center'>
                  <Link
                    to={`${generateUrl(ROUTES.COMPANY_TRACKER_SURVEY, { initiativeId, surveyId: value.surveyId, page: 'question' })}/${value.utrvId}`}
                    className='strong ms-1'
                  >
                    <i className='fas fa-external-link-alt me-1' />
                    Go To Report
                  </Link>
                </div>
              </div>
              <div className='border background-ThemeInfoExtralight p-2'>
                <UniversalTrackerValueView
                  utr={reference.utr}
                  utrv={value.utrv}
                  valueType={value.utrv.valueType as string}
                  display={'normal'}
                  showTableDownload={false}
                />
              </div>
            </div>
          ))}

          {isUserStaff && reference.promptContext && (
            <div className='mt-1'>
              <Button
                color='link'
                onClick={() => setPromptContextOpen(!promptContextOpen)}
              >
                <i className={`fas fa-chevron-${promptContextOpen ? 'up' : 'down'} me-1`} />
                {promptContextOpen ? 'Hide' : 'Show'} prompt context
                <FeatureStability stability={'internal'} />
              </Button>
              <Collapse isOpen={promptContextOpen}>
                <PromptContext promptContext={reference.promptContext} />
              </Collapse>
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};

const PromptContext = ({ promptContext }: { promptContext: string }) => {
  const parsedPromptContext = useMemo(() => {
    try {
      return JSON.stringify(JSON.parse(promptContext), null, 2);
    } catch {
      return promptContext;
    }
  }, [promptContext]);

  return (
    <pre className='mt-2 p-2 border rounded text-sm background-ThemeBgAlt text-ThemeTextMedium' style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
      {parsedPromptContext}
    </pre>
  );
};
